import React, { useState, useCallback } from 'react';
import { 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { Search, Person, Business, LocationOn, Add } from '@mui/icons-material';
import npiRegistryService from '../../services/npiRegistryService';
import { getAuthToken } from '../../utils/authUtils';

const NPIProviderSearch = ({ onProviderSelect }) => {
  const [searchParams, setSearchParams] = useState({
    searchType: 'name',
    firstName: '',
    lastName: '',
    organizationName: '',
    npiNumber: '',
    city: '',
    state: '',
    postalCode: '',
    taxonomyDescription: ''
  });
  
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);

  const handleInputChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSearch = useCallback(async () => {
    const validateSearchParams = () => {
      const { searchType, firstName, lastName, organizationName, npiNumber, city, state, postalCode, taxonomyDescription } = searchParams;

      switch (searchType) {
        case 'npi':
          if (!npiNumber.trim()) {
            setError('NPI number is required for NPI search.');
            return false;
          }
          break;

        case 'name':
          if (!firstName.trim() && !lastName.trim() && !organizationName.trim()) {
            setError('At least one name field is required for name search.');
            return false;
          }
          break;

        case 'location':
          if (!city.trim() && !state.trim() && !postalCode.trim()) {
            setError('At least one location field is required for location search.');
            return false;
          }
          break;

        case 'specialty':
          if (!taxonomyDescription.trim()) {
            setError('Specialty/taxonomy description is required for specialty search.');
            return false;
          }
          break;

        default:
          setError('Please select a valid search type.');
          return false;
      }

      return true;
    };

    if (!validateSearchParams()) {
      return;
    }

    setLoading(true);
    setError('');
    setSearchResults([]);

    try {
      let results = [];
      const idToken = await getAuthToken();
      if (!idToken) throw new Error('User not authenticated');

      switch (searchParams.searchType) {
        case 'npi':
          if (searchParams.npiNumber) {
            const provider = await npiRegistryService.getProviderByNPI(idToken, searchParams.npiNumber);
            results = provider ? [provider] : [];
          }
          break;

        case 'name':
          results = await npiRegistryService.searchProvidersByName(
            idToken,
            searchParams.firstName || undefined,
            searchParams.lastName || undefined,
            searchParams.organizationName || undefined
          );
          break;

        case 'location':
          results = await npiRegistryService.searchProvidersByLocation(
            idToken,
            searchParams.city || undefined,
            searchParams.state || undefined,
            searchParams.postalCode || undefined,
            searchParams.taxonomyDescription || undefined
          );
          break;

        case 'specialty':
          if (searchParams.taxonomyDescription) {
            results = await npiRegistryService.searchProvidersBySpecialty(
              idToken,
              searchParams.taxonomyDescription,
              searchParams.state || undefined
            );
          }
          break;

        default:
          throw new Error('Invalid search type');
      }

      setSearchResults(results);
      
      if (results.length === 0) {
        setError('No providers found matching your search criteria.');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'An error occurred while searching for providers.');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    if (onProviderSelect) {
      onProviderSelect(provider);
    }
  };

  const renderSearchFields = () => {
    const { searchType } = searchParams;

    switch (searchType) {
      case 'npi':
        return (
          <TextField
            fullWidth
            size="small"
            label="NPI Number"
            value={searchParams.npiNumber}
            onChange={(e) => handleInputChange('npiNumber', e.target.value)}
            placeholder="Enter 10-digit NPI number"
            inputProps={{ maxLength: 10 }}
          />
        );

      case 'name':
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="First Name"
                value={searchParams.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Last Name"
                value={searchParams.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Organization ..."
                value={searchParams.organizationName}
                onChange={(e) => handleInputChange('organizationName', e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 'location':
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="City"
                value={searchParams.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="State"
                value={searchParams.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="e.g., CA, NY"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Postal Code"
                value={searchParams.postalCode}
                onChange={(e) => handleInputChange('postalCode', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Specialty (Optional)"
                value={searchParams.taxonomyDescription}
                onChange={(e) => handleInputChange('taxonomyDescription', e.target.value)}
                placeholder="e.g., Family Medicine, Cardiology"
              />
            </Grid>
          </Grid>
        );

      case 'specialty':
        return (
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Specialty/Taxonomy"
                value={searchParams.taxonomyDescription}
                onChange={(e) => handleInputChange('taxonomyDescription', e.target.value)}
                placeholder="e.g., Family Medicine, Internal Medicine"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="State (Optional)"
                value={searchParams.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="e.g., CA, NY"
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  const renderProviderCard = (provider, index) => {
    const displayName = npiRegistryService.getProviderDisplayName(provider);
    const primaryAddress = npiRegistryService.getPrimaryAddress(provider);
    const primaryTaxonomy = npiRegistryService.getPrimaryTaxonomy(provider);
    const formattedAddress = primaryAddress ? npiRegistryService.formatAddress(primaryAddress) : 'No address';

    return (
      <Box 
        key={provider.number} 
        sx={{ 
          mb: 1, 
          p: 1.5, 
          bgcolor: '#f8fafc', 
          borderRadius: 1, 
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': { bgcolor: '#f1f5f9' }
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1} sx={{ minWidth: 0 }}>
            <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
              {provider.enumeration_type === 'NPI-1' ? 
                <Person sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} /> : 
                <Business sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
              }
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {displayName}
              </Typography>
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              NPI: {provider.number} • {provider.enumeration_type === 'NPI-1' ? 'Individual' : 'Organization'}
            </Typography>

            {primaryTaxonomy && (
              <Chip 
                label={primaryTaxonomy.desc} 
                size="small" 
                sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
              />
            )}

            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', mt: 0.5, color: 'text.secondary' }}>
              <LocationOn sx={{ fontSize: 12, mr: 0.25 }} />
              {formattedAddress}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={() => handleProviderSelect(provider)}
            sx={{ ml: 1, minWidth: 60, textTransform: 'none', fontSize: '0.75rem' }}
          >
            Select
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        NPI Provider Search
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={1.5}>
          {/* First row: Search Type only */}
          <Grid item xs={12}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Search Type</InputLabel>
              <Select
                value={searchParams.searchType}
                label="Search Type"
                onChange={(e) => handleInputChange('searchType', e.target.value)}
              >
                <MenuItem value="name">By Name</MenuItem>
                <MenuItem value="npi">By NPI Number</MenuItem>
                <MenuItem value="location">By Location</MenuItem>
                <MenuItem value="specialty">By Specialty</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Second row: Dynamic search fields */}
          <Grid item xs={12}>
            {renderSearchFields()}
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                size="small"
                startIcon={loading ? <CircularProgress size={16} /> : <Search />}
                onClick={handleSearch}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {loading ? 'Searching...' : 'Search Providers'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }} size="small">
          {error}
        </Alert>
      )}

      {searchResults.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Results ({searchResults.length} found)
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            {searchResults.map((provider, index) => renderProviderCard(provider, index))}
          </Box>
        </Box>
      )}

      {selectedProvider && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
            Selected Provider
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {npiRegistryService.getProviderDisplayName(selectedProvider)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            NPI: {selectedProvider.number}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NPIProviderSearch; 